/**
 * Root view. Phase-gated rendering (rather than URL routing) keeps lock-state
 * transitions unambiguous in the shell: the UMK lives in memory only, so a hard
 * lock simply drops you back to `/unlock`. Full route-based IA (portal-spec §3)
 * lands as the next Phase-3 increment.
 */
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useSession } from './store/session';
import { VaultDataProvider, useVaultData } from './store/vault-data';
import { Shell } from './components/Shell';
import { RecoveryCodeModal } from './components/RecoveryCodeModal';
import { Button, Card, ErrorNote } from './components/ui';
import { Login } from './routes/Login';
import { AuthCallback } from './routes/AuthCallback';
import { Welcome } from './routes/Welcome';
import { Unlock } from './routes/Unlock';
import { Recovery } from './routes/Recovery';
import { WipeConfirm } from './routes/WipeConfirm';
import { VaultOverview } from './routes/VaultOverview';
import { ItemDetail } from './routes/ItemDetail';

function Centered({ children }: { children: ReactNode }) {
  return <div className="grid min-h-full place-items-center p-4">{children}</div>;
}

/** `/vaults` → the first vault, once the vault list has loaded. */
function VaultsIndex() {
  const { vaults, loading } = useVaultData();
  if (vaults.length === 0) {
    return (
      <p className="text-sm text-text-secondary">{loading ? 'Loading vaults…' : 'No vaults.'}</p>
    );
  }
  return <Navigate to={`/vaults/${vaults[0]?.id}`} replace />;
}

/** Unlocked app: URL-routed vaults + item detail, inside the persistent Shell. */
function UnlockedApp() {
  return (
    <BrowserRouter>
      <VaultDataProvider>
        <Routes>
          <Route element={<Shell />}>
            <Route index element={<Navigate to="/vaults" replace />} />
            <Route path="vaults" element={<VaultsIndex />} />
            <Route path="vaults/:vaultId" element={<VaultOverview />} />
            <Route path="vaults/:vaultId/items/:itemId" element={<ItemDetail />} />
            <Route path="*" element={<Navigate to="/vaults" replace />} />
          </Route>
        </Routes>
      </VaultDataProvider>
    </BrowserRouter>
  );
}

export function App() {
  const phase = useSession((s) => s.phase);
  const init = useSession((s) => s.init);
  const error = useSession((s) => s.error);
  const dismissError = useSession((s) => s.dismissError);
  const recoveryCode = useSession((s) => s.recoveryCode);

  // The OIDC redirect lands here (no react-router). Handle it before any
  // phase/session logic — the callback exchanges the code and replaces the URL.
  const onCallback = window.location.pathname === '/auth/callback';
  // The emailed wipe link lands on /wipe?token=… — a standalone, GET-safe confirmation page
  // (portal-spec §4.12 / INV-16). Handled outside the phase machine: it must work while locked
  // and mutate nothing until the user explicitly confirms.
  const onWipe = window.location.pathname === '/wipe';

  useEffect(() => {
    if (!onCallback && !onWipe) {
      void init();
    }
  }, [init, onCallback, onWipe]);

  if (onCallback) {
    return <AuthCallback />;
  }

  if (onWipe) {
    return <WipeConfirm />;
  }

  return (
    <>
      {phase === 'loading' && (
        <Centered>
          <p className="text-text-secondary">Loading your vault…</p>
        </Centered>
      )}

      {phase === 'error' && (
        <Centered>
          <Card className="w-full max-w-md space-y-3">
            <h1 className="text-lg font-semibold text-text">Something went wrong</h1>
            {error !== null && <ErrorNote>{error}</ErrorNote>}
            <Button
              onClick={() => {
                dismissError();
                void init();
              }}
            >
              Retry
            </Button>
          </Card>
        </Centered>
      )}

      {phase === 'login' && <Login />}
      {phase === 'welcome' && <Welcome />}
      {phase === 'unlock' && <Unlock />}
      {phase === 'recovery' && <Recovery />}
      {phase === 'unlocked' && <UnlockedApp />}

      {recoveryCode !== null && <RecoveryCodeModal code={recoveryCode} />}
    </>
  );
}
